import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { RootState, AppDispatch } from '../../store/store';
import { setHighlightedObjective } from '../../store/map3UISlice';
import { DrawerPager } from './DrawerPager';

type ObjectivePagerProps = {
  ownerName: string;
  mapShortId: string;
};

/**
 * Pager component for navigating between objectives in drawer order.
 * - Reads objectiveUuids ordering from the milestoneMap in Redux
 * - Resolves current position via ?objectiveId= from search params
 * - Preserves current tab and all other search params on navigation (functional merge)
 * - Dispatches setHighlightedObjective when highlight is active
 */
export function ObjectivePager({ ownerName: _ownerName, mapShortId }: ObjectivePagerProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();

  const objectiveShortId = searchParams.get('objectiveId') ?? undefined;

  // Get the ordered list of objective UUIDs from the map
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((m) => m.shortId === mapShortId),
  );

  // Get all objectives to map uuid -> shortId
  const objectives = useSelector((state: RootState) => state.map3Data.objectives);

  // Current highlight state
  const highlightedObjectiveUuid = useSelector(
    (state: RootState) => state.map3UI.highlightedObjectiveUuid,
  );

  const objectiveUuids = milestoneMap?.objectiveUuids ?? [];

  // Find the current index based on objectiveShortId
  const currentObjective = objectives.find((o) => o.shortId === objectiveShortId);
  const currentIndex = currentObjective
    ? objectiveUuids.indexOf(currentObjective.uuid)
    : -1;

  const navigateToObjective = (targetUuid: string) => {
    const targetObjective = objectives.find((o) => o.uuid === targetUuid);
    if (!targetObjective) return;

    // If highlight is active, update it to the new objective
    if (highlightedObjectiveUuid !== null) {
      dispatch(setHighlightedObjective(targetUuid));
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('objectiveId', targetObjective.shortId);
      // Preserve tab and other params; clear result-specific params
      next.delete('resultId');
      return next;
    });
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    navigateToObjective(objectiveUuids[currentIndex - 1]);
  };

  const handleNext = () => {
    if (currentIndex < 0 || currentIndex >= objectiveUuids.length - 1) return;
    navigateToObjective(objectiveUuids[currentIndex + 1]);
  };

  return (
    <DrawerPager
      currentIndex={currentIndex}
      total={objectiveUuids.length}
      onPrev={handlePrev}
      onNext={handleNext}
      label="Objective navigation"
    />
  );
}
